import { LegalLinkLine } from '@/components/legal/legal-document';
import { AnswerBox } from '@/components/seo/answer-box';
import { breadcrumbJsonLd, LinkList, SectionTitle, SeoSection } from '@/components/seo/seo-blocks';
import { SeoHero } from '@/components/seo/seo-hero';
import { SeoPageShell } from '@/components/seo/seo-page-shell';
import { SeoHead, SITE_URL } from '@/components/seo-head';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import {
  CONTACT_ANSWER,
  CONTACT_DESCRIPTION,
  CONTACT_PATH,
  CONTACT_SOCIAL,
  CONTACT_SUBJECTS,
  CONTACT_TITLE,
  mailtoFor,
} from '@/content/legal/contact';
import { COMPANY } from '@/content/facts';
import { pageTitle } from '@/lib/seo/meta';
import React from 'react';
import { Linking, View } from 'react-native';

/**
 * Contato (`/contato`): e-mail, horário, prazo e o que mandar em cada
 * assunto. O `/suporte` continua como central de dúvidas.
 */
export default function ContactScreen() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      '@id': `${SITE_URL}${CONTACT_PATH}#page`,
      url: `${SITE_URL}${CONTACT_PATH}`,
      name: CONTACT_TITLE,
      description: CONTACT_DESCRIPTION,
      inLanguage: 'pt-BR',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      mainEntity: {
        '@id': `${SITE_URL}/#organization`,
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: COMPANY.email,
          availableLanguage: 'pt-BR',
          areaServed: 'BR',
          hoursAvailable: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '09:00',
            closes: '18:00',
          },
        },
      },
    },
    breadcrumbJsonLd(SITE_URL, [
      { name: 'Início', path: '/' },
      { name: 'Contato', path: CONTACT_PATH },
    ]),
  ];

  return (
    <SeoPageShell
      screenTitle="Contato"
      dockLabel="Organize seus clientes e pagamentos"
      head={
        <SeoHead
          title={pageTitle(CONTACT_TITLE)}
          description={CONTACT_DESCRIPTION}
          path={CONTACT_PATH}
          jsonLd={jsonLd}
        />
      }
    >
      <SeoHero
        width="narrow"
        crumbs={[{ label: 'Início', href: '/' }, { label: 'Contato' }]}
        title={CONTACT_TITLE}
      >
        <View className="mx-auto w-full max-w-4xl">
          <AnswerBox text={CONTACT_ANSWER} />
        </View>
      </SeoHero>

      <SeoSection narrow>
        <SectionTitle title="Como falar com a Controlapag?" />
        <View className="gap-3 rounded-2xl border border-neutral-900/5 bg-card p-6">
          <ThemedText className="text-base leading-relaxed text-neutral-700">
            Todo atendimento é por e-mail, em {COMPANY.email}. Não há atendimento por telefone nem por
            chat. Escrevendo do e-mail da sua conta, a resposta sai mais rápido.
          </ThemedText>
          <ThemedText className="text-base text-neutral-700">
            Horário: {COMPANY.supportHours} (horário de Brasília).
          </ThemedText>
          <ThemedText className="text-base text-neutral-700">
            Prazo de resposta: {COMPANY.supportResponse}.
          </ThemedText>
          <ThemedText className="text-base text-neutral-700">
            Endereço: {COMPANY.city}/{COMPANY.region}, Brasil.
          </ThemedText>
          <Button
            title="Escrever para a Controlapag"
            className="mt-2 self-start px-6"
            onPress={() => void Linking.openURL(mailtoFor('Contato pelo site')).catch(() => {})}
          />
        </View>
      </SeoSection>

      <SeoSection tone="white" narrow>
        <SectionTitle title="O que mandar em cada assunto?" />
        <View className="gap-4">
          {CONTACT_SUBJECTS.map((subject) => (
            <View key={subject.id} className="gap-2 rounded-2xl border border-neutral-900/5 bg-background p-5">
              <ThemedText role="heading" aria-level={3} className="font-semibold text-lg">
                {subject.title}
              </ThemedText>
              <ThemedText className="text-base leading-relaxed text-neutral-700">
                Quando escrever: {subject.when}
              </ThemedText>
              <ThemedText className="text-base leading-relaxed text-neutral-700">
                O que mandar: {subject.send}
              </ThemedText>
              <LegalLinkLine
                link={{ label: `Escrever sobre ${subject.title.toLowerCase()}`, url: mailtoFor(subject.emailSubject) }}
              />
            </View>
          ))}
        </View>
      </SeoSection>

      <SeoSection narrow>
        <SectionTitle title="Onde mais encontrar a Controlapag?" />
        <ThemedText className="mb-4 text-base leading-relaxed text-neutral-700">
          Nas redes, a Controlapag publica dicas para quem presta serviço. Pedido de conta ou de
          dados vai sempre pelo e-mail.
        </ThemedText>
        <View className="mb-8 gap-1">
          {CONTACT_SOCIAL.map((item) => (
            <LegalLinkLine key={item.url} link={{ label: `${item.label} da Controlapag`, url: item.url }} />
          ))}
        </View>
        <LinkList
          items={[
            { label: 'Central de suporte: perguntas frequentes', href: '/suporte' },
            { label: 'Política de privacidade', href: '/privacidade' },
            { label: 'Taxas e prazos do recebimento pelo link', href: '/ajuda/recebimentos' },
            { label: 'Sobre a Controlapag', href: '/sobre' },
          ]}
        />
      </SeoSection>
    </SeoPageShell>
  );
}
